import { appRoles, appRoleSets, appRoleSetTypes } from 'services/Auth/appRoles';
import { IdentityProviderType } from 'models/identity/identityProvider';

export enum UserTypes {
  Member = 0,
  Guest = 1,
  Application = 2,
}

export default class User {
  id: string;

  tenantId?: string;

  name: string;

  email: string;

  created?: Date;

  lastLogin?: Date;

  hasLicense: boolean = false;

  //may sign in: licensed, not deleted, no application; Administration > Users switches it
  isLoginAllowed: boolean = false;

  userType?: UserTypes;

  userTimeZoneCode?: string;

  userLanguageCode?: string;

  //the ids of the organization's roles the user has
  roleIds?: string[];

  systemRoleIds?: number[];

  //the identity behind this user: the provider and its own id for the account (the object id for Entra)
  identityProviderType: IdentityProviderType;

  providerUserId?: string;

  // internal props

  photo: Blob | undefined;

  photoURL: string | undefined;

  constructor(id: string, email: string, name: string) {
    this.id = id;
    this.name = name;
    this.email = email;
    this.userType = UserTypes.Member;
    this.identityProviderType = IdentityProviderType.Entra;
  }

  clone(): User {
    const newUser = new User(this.id, this.email, this.name);
    newUser.tenantId = this.tenantId;
    newUser.userType = this.userType;
    newUser.identityProviderType = this.identityProviderType;
    newUser.providerUserId = this.providerUserId;
    newUser.created = this.created;
    newUser.lastLogin = this.lastLogin;
    newUser.hasLicense = this.hasLicense;
    newUser.isLoginAllowed = this.isLoginAllowed;
    newUser.userTimeZoneCode = this.userTimeZoneCode;
    newUser.userLanguageCode = this.userLanguageCode;
    newUser.roleIds = this.roleIds ? [...this.roleIds] : undefined;
    newUser.systemRoleIds = this.systemRoleIds ? [...this.systemRoleIds] : undefined;
    newUser.photo = this.photo?.slice();
    newUser.photoURL = this.photoURL;

    return newUser;
  }

  static getEmptyUser(id?: string) {
    const newUser = new User('', '', '');
    if (id) newUser.id = id;

    return newUser;
  }

  hasPaidLicense = (): boolean => {
    const sr = this.systemRoleIds ?? [];

    return sr.some((role) => appRoleSets[appRoleSetTypes.Licensed].includes(role));
  };

  isAdmin = (): boolean => {
    const sr = this.systemRoleIds ?? [];

    return sr.includes(appRoles.Admin);
  };

  getPhotoURLObj(): string | undefined {
    if (this.photo) {
      if (!this.photoURL) {
        this.photoURL = URL.createObjectURL(this.photo);
      }

      return this.photoURL;
    }

    return undefined;
  }

  isAppType(): boolean {
    return this.userType === UserTypes.Application;
  }
}

/** The name to show for a user: their own name, or their email when it is blank. */
export function getUserDisplayName(user: User): string {
  return user.name && user.name.length > 0 ? user.name : user.email;
}
